import React, { useState, useEffect } from 'react';
import { useDemoMode } from '../contexts/DemoModeContext';
import { usePricing } from '../contexts/PricingContext';

export const PositionDetailsModal = ({ isOpen, onClose }) => {
  const { demoPortfolio, updateDemoPortfolio } = useDemoMode();
  const { btcPrice } = usePricing();
  const [selectedPosition, setSelectedPosition] = useState(null);

  const calculateTimeLeft = (expiry) => {
    const now = new Date();
    const expiryDate = new Date(expiry);
    const diff = expiryDate - now;
    
    if (diff <= 0) return 'EXPIRED';
    
    const totalMinutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    // Optimized for short-term options (2HR-12HR)
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const calculatePnL = (position) => {
    if (!btcPrice) return 0;
    
    const costBasis = position.premium * position.quantity;
    let currentValue = 0;
    
    if (position.type === 'call') {
      currentValue = Math.max(0, btcPrice - position.strike) * position.quantity;
    } else {
      currentValue = Math.max(0, position.strike - btcPrice) * position.quantity;
    }
    
    return position.status === 'short' ? costBasis - currentValue : currentValue - costBasis;
  };

  const handleExitPosition = (position) => {
    const pnl = calculatePnL(position);
    const newBalance = demoPortfolio.balance + pnl;
    
    // Remove from positions
    const updatedPositions = demoPortfolio.positions.filter(p => p.id !== position.id);
    
    // Add to closed trades
    const closedTrade = {
      ...position,
      exitPrice: btcPrice,
      exitTime: new Date().toISOString(),
      finalPnL: pnl,
      status: 'closed'
    };
    
    const updatedTrades = [...(demoPortfolio.trades || []), closedTrade];
    
    updateDemoPortfolio({
      positions: updatedPositions,
      trades: updatedTrades,
      balance: newBalance
    });
    
    alert(`Position Closed!\nFinal P&L: $${pnl.toLocaleString()}\nNew Balance: $${newBalance.toLocaleString()}`);
    onClose();
  };

  if (!isOpen) return null;

  const positions = demoPortfolio.positions || [];
  
  console.log('📊 POSITION MODAL RECEIVES:', {
    'Raw demoPortfolio': demoPortfolio,
    'Positions array': positions,
    'Positions length': positions.length,
    'First position': positions[0],
    'Modal isOpen': isOpen,
    'All trades': demoPortfolio.trades || [],
    'Trades length': (demoPortfolio.trades || []).length,
    'Position statuses': positions.map(pos => ({ id: pos.id, status: pos.status, side: pos.side }))
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-atticus-navy-900 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-600">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-white">Active Positions</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {positions.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              <div className="text-lg font-medium">No Active Positions</div>
              <div className="text-sm">Execute trades to see positions here</div>
            </div>
          ) : (
            <div className="space-y-4">
              {positions.map((position) => {
                const pnl = calculatePnL(position);
                const timeLeft = calculateTimeLeft(position.expiry);
                
                return (
                  <div key={position.id} className="bg-atticus-navy-800 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="text-white font-bold text-lg">
                          {position.side.toUpperCase()} {position.quantity} {position.type.toUpperCase()}
                        </div>
                        <div className="text-gray-400">Strike: ${position.strike.toLocaleString()}</div>
                      </div>
                      <div className="text-right">
                        <div className={`font-bold text-lg ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {pnl >= 0 ? '+' : ''}${pnl.toLocaleString()}
                        </div>
                        <div className="text-gray-400 text-sm">P&L</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-gray-400">Entry Price:</span>
                        <span className="text-white ml-2">${position.entryPrice?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Current Price:</span>
                        <span className="text-white ml-2">${btcPrice?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Premium Paid:</span>
                        <span className="text-white ml-2">${position.premium.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Time to Expiry:</span>
                        <span className={`ml-2 ${timeLeft === 'EXPIRED' ? 'text-red-400' : 'text-white'}`}>
                          {timeLeft}
                        </span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleExitPosition(position)}
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 rounded transition-colors"
                    >
                      Exit Position
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
